<template>
  <Tabs
    class="bid-url"
    v-model="saleType"
    :items="[
      { id: 'self', label: $t('residentialSaleSelf') },
      { id: 'broker', label: $t('residentialSaleWithBroker') },
    ]"
    theme="border"
  >
    <template #before>
      <Paywall
        :value="addons"
        items="project:bid"
        :action="addon"
        :disabled="saleType === 'broker'"
        :options="{ units }"
        class="residential-mutate-sale__bid-contract-input"
      >
        <InputComponent
          v-model="bidUrl"
          :theme="hasBidAddon && bidUrl ? 'background' : 'default'"
          :disabled="hasBidAddon"
          :is-valid="$path('fields.bidUrl', validator) ? $path('fields.sale.bidUrl.valid', validator) : true"
          :validation-message="$path('fields.bidUrl.error', validator)"
          data-field="bidUrl"
          v-bind="attributes"
        >
          <template #right>
            <CopyToClipboard :value="'https://' + value" />
          </template>

          <template #below>
            <Alert class="mt-2" :theme="hasBidAddon && !hasBidContractTemplates ? 'error' : undefined">
              <template v-if="!hasBidAddon">{{ $t('residentialSaleWithBrokerBidHelperText') }}</template>
              <template v-if="hasBidAddon && hasBidContractTemplates">{{
                $t('residentialSaleBidHelperText')
              }}</template>
              <template v-if="hasBidAddon && !hasBidContractTemplates">
                <div class="row g-2 between">
                  <div style="white-space: pre-wrap">{{ $t('bidContractMissingShowBidUrl') }}</div>
                  <ButtonComponent
                    tag="router-link"
                    :to="{ name: 'project-edit', params: { id: $path('project.id', item), step: 'sale' } }"
                    :label="`${$t('upload')} ${$t('bidContract').toLowerCase()}`"
                    :icon="['fal', 'external-link']"
                    theme="error"
                  />
                </div>
              </template>
            </Alert>
            <Alert v-if="saleType === 'broker' && hasBidAddon" theme="warning" class="mt-1">{{
              $t('residentialSaleBidActiveHelperText')
            }}</Alert>
          </template>
        </InputComponent>
      </Paywall>
    </template>
  </Tabs>
</template>

<script>
import { UrlTransformer } from '@/utils/transformers'
import { mapState, mapGetters } from 'vuex'
import { i18n } from '@/i18n'
import CopyToClipboard from '@/components/CopyToClipboard'
import { InputComponent } from 'vue-elder-input'
import Tabs from '@/components/Tabs'
import { Paywall } from '@/components/Upsell'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Bus from '@/bus'

const meta = {
  label: i18n.t('bidUrl'),
}

export { meta }
export default {
  props: {
    value: String,
    data: {
      type: Object,
      default: () => ({}),
    },
    validator: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      saleType: 'self',
    }
  },
  computed: {
    ...mapState('Residential', {
      item: state => state.item,
      addons(state) {
        return this.$path('item.project.metadata.addons', state) || []
      },
      project: state => state.item.project,
      units(state) {
        return this.$path('item.project.metadata.limits.units', state)
      },
    }),
    bidUrl: UrlTransformer('value', { lowerCase: false, emit: true }),
    bid() {
      return this.$path('item.contract.bid', this)
    },
    hasBidContractTemplates() {
      return this.$path('contractTemplates.bid.url', this.project)
    },
    hasBidAddon() {
      return this.addons.includes('project:bid')
    },
    hasPresentation() {
      return (
        this.addons.includes('project:presentation') ||
        this.$store.state.enums.ClientDetails.contract === 'newhome-flatrate'
      )
    },
    label() {
      return meta.label
    },
    status: {
      get() {
        return this.data.status
      },
      set(val) {
        if (this.hasBidAddon && this.hasPresentation && val === 'sold') return (this.showSoldDialog = true)
        if (this.addons.includes('project:shop-purchase') && ['draft', 'sold'].includes(val))
          return (this.data.status = val)

        if (!this.hasPresentation && !['draft', 'upcoming'].includes(val))
          return Bus.emit('pricing:show', {
            addon: 'project:presentation',
            options: { units: this.units },
            action: () => this.addon('project:presentation'),
          })
            .then(() => (this.data.status = val))
            .catch(() => (this.$refs.status.$el.querySelector('select').value = this.status))

        this.value.status = val
      },
    },
    attributes() {
      let attrs = {
        ...this.$attrs,
        prefix: 'https://',
        label: this.label,
      }

      return attrs
    },
  },
  methods: {
    addon(addon) {
      return this.$store
        .dispatch('Project/addon', {
          addon,
          state: true,
          project: this.project.id,
        })
        .then(() => this.addons.push(addon))
    },
  },
  components: {
    InputComponent,
    CopyToClipboard,
    Tabs,
    Paywall,
    Alert,
  },
}
</script>

<style lang="scss">
.bid-url {
  .paywall {
    margin: 1rem;
  }
}
</style>
